import { useState } from 'react';
import { Box, Grid, Modal, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const EventGallery = ({ images, eventId }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Don't render if no images
  if (!images || images.length === 0) {
    return null;
  }

  // Build full image paths
  const getImagePath = (filename) => `${filename}`;

  const handleThumbnailClick = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const handleClose = () => {
    setLightboxOpen(false);
  };

  const handlePrevious = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowLeft') handlePrevious();
    if (e.key === 'ArrowRight') handleNext();
    if (e.key === 'Escape') handleClose();
  };

  return (
    <>
      {/* Thumbnail Grid */}
      <Grid container spacing={2}>
        {images.map((image, index) => (
          <Grid size={{ xs: 6, sm: 4, md: 3 }} key={index}>
            <Box
              onClick={() => handleThumbnailClick(index)}
              sx={{
                position: 'relative',
                paddingBottom: '75%', // 4:3 aspect ratio
                overflow: 'hidden',
                borderRadius: 1,
                cursor: 'pointer',
                boxShadow: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-4px)',
                },
              }}
            >
              <Box
                component="img"
                src={getImagePath(image)}
                alt={`Event photo ${index + 1}`}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Lightbox Modal */}
      <Modal
        open={lightboxOpen}
        onClose={handleClose}
        onKeyDown={handleKeyDown}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            maxWidth: '90vw',
            maxHeight: '90vh',
            outline: 'none',
          }}
        >
          {/* Close Button */}
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              top: -50,
              right: 0,
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
              },
              zIndex: 2,
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Image Counter */}
          <Typography
            sx={{
              position: 'absolute',
              top: -50,
              left: 0,
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              padding: '8px 16px',
              borderRadius: 1,
              fontWeight: 600,
            }}
          >
            {currentImageIndex + 1} / {images.length}
          </Typography>

          {/* Main Image */}
          <Box
            component="img"
            src={getImagePath(images[currentImageIndex])}
            alt={`Event photo ${currentImageIndex + 1}`}
            sx={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              objectFit: 'contain',
              boxShadow: 24,
              borderRadius: 1,
            }}
          />

          {/* Navigation Buttons */}
          {images.length > 1 && (
            <>
              <IconButton
                onClick={handlePrevious}
                sx={{
                  position: 'absolute',
                  left: -60,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'white',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  },
                }}
              >
                <ChevronLeftIcon fontSize="large" />
              </IconButton>

              <IconButton
                onClick={handleNext}
                sx={{
                  position: 'absolute',
                  right: -60,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'white',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  },
                }}
              >
                <ChevronRightIcon fontSize="large" />
              </IconButton>
            </>
          )}
        </Box>
      </Modal>
    </>
  );
};

export default EventGallery;